import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp, TrendingDown, DollarSign, Users, BarChart3, PieChart, Calendar, FileSpreadsheet, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, Area, AreaChart, ComposedChart } from 'recharts';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import { salesPerformanceService } from '../../services/salesPerformanceService';
import { useAuth } from '../../contexts/AuthContext';
import MonthlyDataForm from './components/MonthlyDataForm';
import MonthlyDataTable from './components/MonthlyDataTable';

import DetailedExcelView from './components/DetailedExcelView';

export default function SalesPerformanceTracker() {
  const { profile } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [records, setRecords] = useState([]);
  const [insights, setInsights] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [recordsData, insightsData, trendsData] = await Promise.all([
        salesPerformanceService?.getAll(),
        salesPerformanceService?.getInsights(),
        salesPerformanceService?.getTrendData()
      ]);

      setRecords(recordsData);
      setInsights(insightsData);
      setTrendData(trendsData);
    } catch (err) {
      setError(err?.message || 'Failed to load sales performance data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingRecord) {
        await salesPerformanceService?.update(editingRecord?.id, data);
      } else {
        await salesPerformanceService?.create(data);
      }
      
      await loadData();
      setShowForm(false);
      setEditingRecord(null);
    } catch (err) {
      throw new Error(err?.message || 'Failed to save data');
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setShowForm(true);
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      await salesPerformanceService?.delete(recordId);
      await loadData();
    } catch (err) {
      setError(err?.message || 'Failed to delete record');
    }
  };

  const calculateMonthlyProfitLoss = (record) => {
    const totalRevenue = (record?.existingClientRevenue || 0) + (record?.newClientRevenue || 0);
    const totalExpenses = (record?.otherMonthlyCosts || 0) + (record?.googleAdsExpense || 0);
    const grossProfit = totalRevenue * ((record?.grossProfitPercentage || 0) / 100);
    const netProfit = grossProfit - totalExpenses;
    const profitMarginPercentage = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    return {
      totalRevenue,
      totalExpenses,
      grossProfit,
      netProfit,
      profitMarginPercentage,
      isProfit: netProfit >= 0
    };
  };

  const calculateROI = (record) => {
    const totalRevenue = (record?.existingClientRevenue || 0) + (record?.newClientRevenue || 0);
    const totalInvestment = (record?.otherMonthlyCosts || 0) + (record?.googleAdsExpense || 0);
    
    if (totalInvestment === 0) {
      return totalRevenue > 0 ? 100 : 0; // If no investment but revenue exists, show 100% ROI
    }
    
    const roi = ((totalRevenue - totalInvestment) / totalInvestment) * 100;
    return roi;
  };

  const formatCurrency = (amount, currency = null) => {
    const displayCurrency = currency || profile?.currency || 'USD';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: displayCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  // Detect issues in the data for visual indicators
  const detectIssues = () => {
    const issues = [];
    
    if (insights) {
      // Check for negative profit margin
      if (insights?.averageProfitMargin < 0) {
        issues?.push({
          type: 'critical',
          title: 'Negative Profit Margin',
          description: `Average profit margin is ${insights?.averageProfitMargin?.toFixed(2)}%`,
          icon: TrendingDown
        });
      }
      
      // Check for low profit margin (below 10%)
      if (insights?.averageProfitMargin >= 0 && insights?.averageProfitMargin < 10) {
        issues?.push({
          type: 'warning',
          title: 'Low Profit Margin',
          description: `Profit margin is only ${insights?.averageProfitMargin?.toFixed(2)}%`,
          icon: TrendingDown
        });
      }
      
      // Check for negative revenue trend
      if (insights?.revenueTrend < 0) {
        issues?.push({
          type: 'warning',
          title: 'Declining Revenue',
          description: `Revenue trend is down ${Math.abs(insights?.revenueTrend)?.toFixed(1)}%`,
          icon: TrendingDown
        });
      }
      
      // Check if expenses exceed revenue significantly
      const expenseRatio = (insights?.totalExpenses / insights?.totalRevenue) * 100;
      if (expenseRatio > 80) {
        issues?.push({
          type: 'critical',
          title: 'High Expense Ratio',
          description: `Expenses are ${expenseRatio?.toFixed(1)}% of revenue`,
          icon: DollarSign
        });
      }
    }
    
    // Check trend data for consecutive declining months
    if (trendData?.length >= 3) {
      const lastThreeMonths = trendData?.slice(-3);
      const decliningRevenue = lastThreeMonths?.every((month, idx) => {
        if (idx === 0) return true;
        return month?.revenue < lastThreeMonths?.[idx - 1]?.revenue;
      });
      
      if (decliningRevenue) {
        issues?.push({
          type: 'critical',
          title: 'Consecutive Revenue Decline',
          description: 'Revenue has declined for 3+ consecutive months',
          icon: TrendingDown
        });
      }
    }
    
    return issues;
  };

  const issues = detectIssues();

  // Prepare enhanced trend data with issue indicators and ROAS
  const enhancedTrendData = trendData?.map(month => {
    const profitMargin = month?.revenue > 0 ? ((month?.netRevenue / month?.revenue) * 100) : 0;
    const hasIssue = profitMargin < 0 || (month?.expenses / month?.revenue) > 0.8 || (month?.roas > 0 && month?.roas < 2.0);
    
    return {
      ...month,
      profitMargin,
      hasIssue,
      issueIndicator: hasIssue ? month?.revenue : null,
      roas: month?.roas || 0
    };
  });

  // Prepare pie chart data for revenue breakdown
  const revenueBreakdownData = insights ? [
    { name: 'Existing Clients', value: insights?.totalExistingClientRevenue || 0, color: '#10b981' },
    { name: 'New Clients', value: insights?.totalNewClientRevenue || 0, color: '#06b6d4' }
  ] : [];

  // Prepare pie chart data for cost breakdown
  const costBreakdownData = insights ? [
    { name: 'Agency Fees', value: insights?.totalAgencyFees || 0, color: '#ef4444' },
    { name: 'PPC Fees', value: insights?.totalPPCFees || 0, color: '#f59e0b' }
  ] : [];

  // Custom label function to prevent text cutoff
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 35; // Increased distance from pie
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    
    const textAnchor = x > cx ? 'start' : 'end';
    
    return (
      <text 
        x={x} 
        y={y} 
        fill="#374151" 
        textAnchor={textAnchor}
        dominantBaseline="central"
        style={{ fontSize: '13px', fontWeight: '600' }}
      >
        <tspan x={x} dy="0">{name}</tspan>
        <tspan x={x} dy="16" style={{ fontSize: '12px', fontWeight: '500', fill: '#6b7280' }}>
          {(percent * 100)?.toFixed(0)}%
        </tspan>
      </text>
    );
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Header />
          <main className="flex-1 overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading sales performance data...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/40">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            {/* Modern Header with gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-xl border border-blue-400/20 p-8 mb-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                      <BarChart3 className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">Sales Tracker</h1>
                  </div>
                  <p className="text-blue-100 text-base ml-16">
                    Track and analyze your sales performance with comprehensive insights and trends
                  </p>
                </div>
                <button
                  onClick={() => {
                    setEditingRecord(null);
                    setShowForm(true);
                  }}
                  className="bg-white text-blue-600 px-6 py-3 rounded-xl flex items-center gap-2 hover:bg-blue-50 transition-all shadow-lg font-semibold"
                >
                  <Plus size={20} />
                  Add Monthly Data
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-gradient-to-r from-red-50 to-red-100/50 border-l-4 border-red-500 rounded-xl shadow-lg">
                <p className="text-red-800 font-semibold">{error}</p>
              </div>
            )}

            {/* Tab Navigation */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 mb-6 overflow-hidden">
              <div className="flex border-b border-gray-200 overflow-x-auto">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all whitespace-nowrap ${
                    activeTab === 'overview' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <BarChart3 size={18} />
                  Overview & Insights
                </button>
                <button
                  onClick={() => setActiveTab('monthly-data')}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all whitespace-nowrap ${
                    activeTab === 'monthly-data' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Calendar size={18} />
                  Monthly Performance Data
                </button>
                <button
                  onClick={() => setActiveTab('excel-view')}
                  className={`flex items-center gap-2 px-6 py-4 font-semibold text-sm transition-all whitespace-nowrap ${
                    activeTab === 'excel-view' ?'text-blue-600 border-b-2 border-blue-600 bg-blue-50' :'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <FileSpreadsheet size={18} />
                  Detailed Excel View
                </button>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && insights && (
              <div className="space-y-6">
                {/* Issue Alerts Banner - Enhanced */}
                {issues?.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-lg border-2 border-red-300 p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                          <AlertTriangle className="text-red-600" size={24} />
                        </div>
                        <div>
                          <h2 className="text-xl font-bold text-red-900">
                            ⚠️ {issues?.length} Issue{issues?.length > 1 ? 's' : ''} Detected
                          </h2>
                          <p className="text-sm text-red-700">Review the following concerns requiring immediate attention</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {issues?.map((issue, idx) => (
                          <div
                            key={idx}
                            className={`p-4 rounded-xl border-2 relative overflow-hidden ${
                              issue?.type === 'critical' ?'bg-gradient-to-br from-red-50 to-red-100 border-red-300' :'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300'
                            }`}
                          >
                            <div className="absolute top-0 right-0 w-24 h-24 bg-white/30 rounded-full -mr-12 -mt-12"></div>
                            <div className="relative z-10 flex items-start gap-3">
                              <issue.icon
                                className={issue?.type === 'critical' ? 'text-red-600' : 'text-yellow-600'}
                                size={20}
                              />
                              <div className="flex-1">
                                <h3
                                  className={`font-bold text-sm ${
                                    issue?.type === 'critical' ? 'text-red-900' : 'text-yellow-900'
                                  }`}
                                >
                                  {issue?.title}
                                </h3>
                                <p
                                  className={`text-xs mt-1 ${
                                    issue?.type === 'critical' ? 'text-red-700' : 'text-yellow-700'
                                  }`}
                                >
                                  {issue?.description}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* KPI Cards Section - Enhanced with ROAS */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 shadow-lg border border-blue-400/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <DollarSign className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm text-blue-100 font-medium">Total Revenue</div>
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {formatCurrency(insights?.totalRevenue)}
                      </div>
                      {insights?.revenueTrend !== undefined && (
                        <div className="flex items-center gap-1 mt-2">
                          {insights?.revenueTrend >= 0 ? (
                            <TrendingUp className="w-4 h-4 text-white" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-white" />
                          )}
                          <span className="text-sm text-white font-medium">
                            {Math.abs(insights?.revenueTrend)?.toFixed(1)}% trend
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl p-5 shadow-lg border border-purple-400/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <Users className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm text-purple-100 font-medium">Total Clients/Orders</div>
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {(insights?.totalNewClients || 0) + (insights?.totalExistingClients || 0)}
                      </div>
                      <div className="text-xs text-purple-100 mt-2">
                        {insights?.totalExistingClients} existing • {insights?.totalNewClients} new
                      </div>
                    </div>
                  </div>

                  {/* NEW: ROAS KPI Card */}
                  <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-2xl p-5 shadow-lg border border-cyan-400/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm text-cyan-100 font-medium">Avg ROAS</div>
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {insights?.averageROAS?.toFixed(2) || '0.00'}x
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {insights?.roasTrend >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-white" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-white" />
                        )}
                        <span className="text-sm text-white font-medium">
                          {insights?.roasTrend >= 0 ? '+' : ''}{insights?.roasTrend?.toFixed(1)}% trend
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg border border-green-400/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <TrendingUp className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm text-green-100 font-medium">Avg Profit Margin</div>
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {insights?.averageProfitMargin?.toFixed(2) || 0}%
                      </div>
                      <div className="flex items-center gap-1 mt-2">
                        {insights?.averageProfitMargin >= 0 ? (
                          <CheckCircle className="w-4 h-4 text-white" />
                        ) : (
                          <XCircle className="w-4 h-4 text-white" />
                        )}
                        <span className="text-sm text-white font-medium">
                          {insights?.averageProfitMargin >= 0 ? 'Positive' : 'Negative'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl p-5 shadow-lg border border-red-400/20 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                    <div className="relative z-10">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                          <TrendingDown className="w-5 h-5 text-white" />
                        </div>
                        <div className="text-sm text-red-100 font-medium">Total Expenses</div>
                      </div>
                      <div className="text-3xl font-bold text-white">
                        {formatCurrency(insights?.totalExpenses)}
                      </div>
                      <div className="text-xs text-red-100 mt-2">
                        Agency: {formatCurrency(insights?.totalAgencyFees || 0)} • PPC: {formatCurrency(insights?.totalPPCFees || 0)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Large Main Performance Graph - Add ROAS line */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50 rounded-full -mr-24 -mt-24"></div>
                  <div className="relative z-10">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                      <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <BarChart3 size={24} className="text-blue-600" />
                          Performance Overview & Trend Analysis
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Comprehensive view of revenue, expenses, profit trends, and ROAS with automated issue detection
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <div className="flex items-center gap-2 bg-blue-50 px-3 py-2 rounded-lg">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-gray-700 font-medium">Revenue</span>
                        </div>
                        <div className="flex items-center gap-2 bg-red-50 px-3 py-2 rounded-lg">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-gray-700 font-medium">Expenses</span>
                        </div>
                        <div className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-lg">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-gray-700 font-medium">Net Profit</span>
                        </div>
                        <div className="flex items-center gap-2 bg-cyan-50 px-3 py-2 rounded-lg">
                          <div className="w-3 h-3 rounded-full bg-cyan-500"></div>
                          <span className="text-gray-700 font-medium">ROAS</span>
                        </div>
                        <div className="flex items-center gap-2 bg-orange-50 px-3 py-2 rounded-lg">
                          <div className="w-3 h-3 bg-red-600 rounded"></div>
                          <span className="text-gray-700 font-medium">Issue Point</span>
                        </div>
                      </div>
                    </div>
                    <ResponsiveContainer width="100%" height={500}>
                      <ComposedChart data={enhancedTrendData}>
                        <defs>
                          <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                          </linearGradient>
                          <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05}/>
                          </linearGradient>
                          <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 13, fill: '#4b5563', fontWeight: 500 }}
                          tickLine={{ stroke: '#9ca3af' }}
                        />
                        <YAxis 
                          yAxisId="left"
                          tick={{ fontSize: 13, fill: '#4b5563', fontWeight: 500 }}
                          tickLine={{ stroke: '#9ca3af' }}
                          tickFormatter={(value) => `${(value / 1000)?.toFixed(0)}k`}
                        />
                        <YAxis 
                          yAxisId="right"
                          orientation="right"
                          tick={{ fontSize: 13, fill: '#06b6d4', fontWeight: 500 }}
                          tickLine={{ stroke: '#06b6d4' }}
                          label={{ value: 'ROAS', angle: -90, position: 'insideRight', fill: '#06b6d4' }}
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            if (name === 'ROAS') return [`${value?.toFixed(2)}x`, name];
                            return [formatCurrency(value), name];
                          }}
                          contentStyle={{ 
                            backgroundColor: '#ffffff',
                            border: '2px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '12px',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconType="circle"
                        />
                        
                        <Area 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#3b82f6" 
                          strokeWidth={3}
                          fill="url(#revenueGradient)"
                          name="Revenue"
                        />
                        <Area 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="expenses" 
                          stroke="#ef4444" 
                          strokeWidth={3}
                          fill="url(#expenseGradient)"
                          name="Expenses"
                        />
                        <Area 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="netRevenue" 
                          stroke="#10b981" 
                          strokeWidth={3}
                          fill="url(#profitGradient)"
                          name="Net Profit"
                        />
                        
                        {/* NEW: ROAS Line */}
                        <Line 
                          yAxisId="right"
                          type="monotone" 
                          dataKey="roas" 
                          stroke="#06b6d4" 
                          strokeWidth={3}
                          dot={{ fill: '#06b6d4', r: 5 }}
                          name="ROAS"
                        />
                        
                        <Line 
                          yAxisId="left"
                          type="monotone" 
                          dataKey="issueIndicator" 
                          stroke="transparent"
                          strokeWidth={0}
                          dot={{ fill: '#dc2626', r: 9, strokeWidth: 3, stroke: '#fff' }}
                          name="Issue Detected"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                    
                    {/* Enhanced Graph Insights with ROAS */}
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl border border-blue-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                            {insights?.revenueTrend >= 0 ? (
                              <TrendingUp className="text-white" size={20} />
                            ) : (
                              <TrendingDown className="text-white" size={20} />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-blue-900 font-semibold">Revenue Trend</p>
                            <p className="text-lg font-bold text-blue-900">
                              {insights?.revenueTrend >= 0 ? '+' : ''}{insights?.revenueTrend?.toFixed(1)}%
                            </p>
                            <p className="text-xs text-gray-700 font-medium mt-1">
                              {insights?.revenueTrend >= 0 ? '📈 Growing momentum' : '📉 Needs attention'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* NEW: ROAS Insight Card */}
                      <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl border border-cyan-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-cyan-500 rounded-xl flex items-center justify-center">
                            {insights?.averageROAS >= 4 ? (
                              <CheckCircle className="text-white" size={20} />
                            ) : (
                              <AlertTriangle className="text-white" size={20} />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-cyan-900 font-semibold">ROAS Health</p>
                            <p className="text-lg font-bold text-cyan-900">
                              {insights?.averageROAS?.toFixed(2)}x
                            </p>
                            <p className="text-xs text-gray-700 font-medium mt-1">
                              {insights?.averageROAS >= 4 ? '✅ Excellent ROAS' : 
                               insights?.averageROAS >= 2 ? '⚠️ Acceptable ROAS' : '❌ Low ROAS'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-4 rounded-xl border border-green-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                            {insights?.averageProfitMargin >= 0 ? (
                              <CheckCircle className="text-white" size={20} />
                            ) : (
                              <XCircle className="text-white" size={20} />
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-green-900 font-semibold">Profit Health</p>
                            <p className="text-lg font-bold text-green-900">
                              {insights?.averageProfitMargin?.toFixed(2)}%
                            </p>
                            <p className="text-xs text-gray-700 font-medium mt-1">
                              {insights?.averageProfitMargin >= 10 ? '✅ Healthy margin' : 
                               insights?.averageProfitMargin >= 0 ? '⚠️ Low margin' : '❌ Negative margin'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                            <AlertTriangle className="text-white" size={20} />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-orange-900 font-semibold">Issue Detection</p>
                            <p className="text-lg font-bold text-orange-900">
                              {enhancedTrendData?.filter(d => d?.hasIssue)?.length} months
                            </p>
                            <p className="text-xs text-gray-700 font-medium mt-1">
                              {enhancedTrendData?.filter(d => d?.hasIssue)?.length === 0 
                                ? '✅ All clear' : '⚠️ Requires review'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Revenue & Cost Breakdown Section - Enhanced */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Revenue Breakdown */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-green-50 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative z-10">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <PieChart size={20} className="text-emerald-600" />
                        Revenue Breakdown
                      </h2>
                      <div className="grid grid-cols-1 gap-3 mb-4">
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 p-4 rounded-xl border border-emerald-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xs font-semibold text-emerald-900 mb-1">Existing Clients</h3>
                              <p className="text-2xl font-bold text-emerald-700">
                                {formatCurrency(insights?.totalExistingClientRevenue || 0)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-emerald-600 font-medium">
                                {insights?.totalExistingClients} orders
                              </p>
                              <p className="text-xs text-emerald-500 mt-1">
                                {((insights?.totalExistingClientRevenue / insights?.totalRevenue) * 100)?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-4 rounded-xl border border-cyan-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xs font-semibold text-cyan-900 mb-1">New Clients</h3>
                              <p className="text-2xl font-bold text-cyan-700">
                                {formatCurrency(insights?.totalNewClientRevenue || 0)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-cyan-600 font-medium">
                                {insights?.totalNewClients} orders
                              </p>
                              <p className="text-xs text-cyan-500 mt-1">
                                {((insights?.totalNewClientRevenue / insights?.totalRevenue) * 100)?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="w-full" style={{ height: '280px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RePieChart>
                            <Pie
                              data={revenueBreakdownData}
                              cx="50%"
                              cy="50%"
                              labelLine={{
                                stroke: '#d1d5db',
                                strokeWidth: 2
                              }}
                              label={renderCustomLabel}
                              outerRadius={75}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {revenueBreakdownData?.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry?.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Cost Breakdown */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full -mr-16 -mt-16"></div>
                    <div className="relative z-10">
                      <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <PieChart size={20} className="text-red-600" />
                        Cost Breakdown
                      </h2>
                      <div className="grid grid-cols-1 gap-3 mb-4">
                        <div className="bg-gradient-to-br from-red-50 to-red-100 p-4 rounded-xl border border-red-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xs font-semibold text-red-900 mb-1">Agency Fees</h3>
                              <p className="text-2xl font-bold text-red-700">
                                {formatCurrency(insights?.totalAgencyFees || 0)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-red-500 mt-1">
                                {((insights?.totalAgencyFees / insights?.totalExpenses) * 100)?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl border border-orange-200">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xs font-semibold text-orange-900 mb-1">PPC Fees</h3>
                              <p className="text-2xl font-bold text-orange-700">
                                {formatCurrency(insights?.totalPPCFees || 0)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-orange-500 mt-1">
                                {((insights?.totalPPCFees / insights?.totalExpenses) * 100)?.toFixed(1)}%
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="w-full" style={{ height: '280px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <RePieChart>
                            <Pie
                              data={costBreakdownData}
                              cx="50%"
                              cy="50%"
                              labelLine={{
                                stroke: '#d1d5db',
                                strokeWidth: 2
                              }}
                              label={renderCustomLabel}
                              outerRadius={75}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {costBreakdownData?.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry?.color} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </RePieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Secondary Trend Charts Section - Enhanced */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-200/60 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-6">Additional Performance Metrics</h2>
                  <div className="space-y-8">
                    {/* Profit Margin Trend - Full Width */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <TrendingUp size={16} className="text-green-600" />
                        Profit Margin Trend Over Time
                      </h3>
                      <ResponsiveContainer width="100%" height={400}>
                        <AreaChart data={enhancedTrendData}>
                          <defs>
                            <linearGradient id="marginGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} tickFormatter={(value) => `${value}%`} />
                          <Tooltip formatter={(value) => `${value?.toFixed(2)}%`} />
                          <Area 
                            type="monotone" 
                            dataKey="profitMargin" 
                            stroke="#10b981" 
                            strokeWidth={3} 
                            fill="url(#marginGradient)"
                            name="Profit Margin (%)"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Client Growth Chart - Full Width */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Users size={16} className="text-purple-600" />
                        Client Growth Analysis
                      </h3>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} />
                          <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="existingClients" fill="#10b981" name="Existing Clients" radius={[8, 8, 0, 0]} />
                          <Bar dataKey="newClients" fill="#06b6d4" name="New Clients" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'monthly-data' && (
              <MonthlyDataTable
                records={records}
                onEdit={handleEdit}
                onDelete={handleDelete}
                formatCurrency={formatCurrency}
                calculateMonthlyProfitLoss={calculateMonthlyProfitLoss}
                calculateROI={calculateROI}
              />
            )}

            {activeTab === 'excel-view' && (
              <DetailedExcelView
                records={records}
                onEdit={handleEdit}
                onDelete={handleDelete}
                formatCurrency={formatCurrency}
                calculateMonthlyProfitLoss={calculateMonthlyProfitLoss}
                calculateROI={calculateROI}
              />
            )}
          </div>
        </main>
      </div>
      {/* Monthly Data Form Modal */}
      {showForm && (
        <MonthlyDataForm
          record={editingRecord}
          onSave={handleSave}
          onCancel={() => {
            setShowForm(false);
            setEditingRecord(null);
          }}
          userCurrency={profile?.currency || 'USD'}
        />
      )}
    </div>
  );
}